import { createSlice } from '@reduxjs/toolkit';
import { DataStore } from '@aws-amplify/datastore';
import {
  PageSettingOverride,
  UserDocumentVersionSettings,
  UserNotationsStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import { commonReducers, initialState, SliceState } from './common';
import ActiveUser from '../../global/ActiveUser';
import * as logger from 'src/utils/logger';

const sliceName = 'userDocumentVersionSettings';
const { reducers, extraReducers, asyncActions } = commonReducers<UserDocumentVersionSettings>(sliceName);

export interface CreateUserDocumentVersionSettingsPayload {
  documentId?: string
  documentVersionId?: string
  customDeckId?: string
  pageSettings: PageSettingOverride[]
}

interface UpdatePageSettingsPayload {
  userDocumentVersionSettings: UserDocumentVersionSettings
  pageSettings: PageSettingOverride[]
}

const userDocumentVersionSettingsSlice = createSlice({
  name: sliceName,
  initialState: initialState<UserDocumentVersionSettings>(),
  reducers: {
    ...reducers,
    createUserDocumentVersionSettings: (
      _state: SliceState<UserDocumentVersionSettings>,
      action: { payload: CreateUserDocumentVersionSettingsPayload },
    ): void => {
      const { payload } = action;
      const user = ActiveUser.user;
      const now = new Date().toISOString();

      if (!user) {
        const errorText = 'Active user is not set when trying to create userDocumentVersionSettings';
        logger.userNotations.error(errorText);
        throw new Error(errorText);
      }

      if (!payload.documentVersionId && !payload.customDeckId) {
        const errorText = 'Cannot create userDocumentVersionSettings without documentVersionId or customDeckId';
        logger.userNotations.error(errorText, payload);
        throw new Error(errorText);
      }

      const userDocumentVersionSettings = new UserDocumentVersionSettings({
        tenantId: user.tenantId,
        createdAt: now,
        createdBy: user.id,
        updatedAt: now,
        updatedBy: user.id,
        documentId: payload.documentId,
        documentVersionId: payload.documentVersionId,
        customDeckId: payload.customDeckId,
        pageSettings: payload.pageSettings,
        status: UserNotationsStatus.ACTIVE,
      });

      DataStore.save(userDocumentVersionSettings);
    },
    updatePageSettings: {
      prepare: (payload: UpdatePageSettingsPayload) => {
        const user = ActiveUser.user;
        const now = new Date().toISOString();

        if (!user) {
          const errorText = 'Active user is not set when trying to update userDocumentVersionSettings';
          logger.userNotations.error(errorText);
          throw new Error(errorText);
        }

        return {
          payload: {
            model: UserDocumentVersionSettings,
            entity: payload.userDocumentVersionSettings,
            updates: {
              updatedAt: now,
              updatedBy: user.id,
              pageSettings: payload.pageSettings,
            },
          },
        };
      },
      reducer: reducers.save,
    },
  },
  extraReducers,
});

export default userDocumentVersionSettingsSlice;
export const userDocumentVersionSettingsActions = {
  ...userDocumentVersionSettingsSlice.actions,
  ...asyncActions,
};
